// An agent as the person chose to see it (pref:ui.agentFace, html[data-faces]; 23 Oct 2026): in the Icon style the call site's own tile,
// unchanged (ModuleDisc, the ribbon-O mark, a desk's numeral, a specialist's glyph), so Classic keeps its markup to the pixel; as a
// Emblem the agent's shape and emblem (packages/ui faces), in the state the conversation is in. `size` is the tile's size; `faceSize`
// gives a face more room where the layout has it (a header, a hero). WP-THEME.
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { ModuleDisc, useFaceStyle, type EconicModuleKey, type FaceState, type ModuleDiscSize } from "../theme/primitives";
import { EconicMark } from "./EconicMark";
import { LazyAgentFace } from "./LazyAgentFace";

const MODULE_KEYS = new Set(["kb", "knowledge", "uae-radar", "global-radar", "outlook", "country360", "policylab"]);
const DISC_SIZES: readonly ModuleDiscSize[] = [16, 20, 22, 26, 28, 30, 32, 38, 44, 52];
const discSize = (px: number): ModuleDiscSize => DISC_SIZES.reduce((a, b) => (Math.abs(b - px) < Math.abs(a - px) ? b : a));

export interface AgentAvatarProps {
  /** The agent's key: a module key, "econic" or null (ECONIC), a specialist's or a desk agent's key. */
  agent: string | null | undefined;
  size: number;
  faceSize?: number;
  state?: FaceState;
  /** The agent's name when the avatar stands alone; decorative otherwise. */
  label?: string;
  /** The Icon style's tile; ECONIC and the six agents have a default (EconicMark, ModuleDisc). */
  icon?: ReactNode;
  /** A generated face's glyph, or the server's icon name, or a desk's number. */
  glyph?: LucideIcon;
  iconName?: string | null;
  desk?: number | null;
  className?: string;
}

export function AgentAvatar({ agent, size, faceSize, state = "idle", label, icon, glyph, iconName, desk, className }: AgentAvatarProps) {
  const style = useFaceStyle();
  if (style === "icon") {
    if (icon !== undefined) return <>{icon}</>;
    if (agent && MODULE_KEYS.has(agent)) return <ModuleDisc module={agent as EconicModuleKey} size={discSize(size)} labelled={!!label} className={className} />;
    return <EconicMark size={size} label={label} className={className} />;
  }
  return <LazyAgentFace agent={agent} size={faceSize ?? size} state={state} label={label} glyph={glyph} icon={iconName} desk={desk} className={className} />;
}
